import React, { useRef, useEffect } from "react";
import * as d3 from "d3";
import { ConnectionChartDataProps } from "./ChartTypes";
import { GeneralContext } from "@services/providers/GeneralProvider";

// import InfoTooltip from "@components/common/InfoTooltip";

interface DonutChartProps {
  parentid: string;
  data: ConnectionChartDataProps;
  linkLength?: number;
  maxNodes?: number;
  nodeType?: string;
  onNodeClick?: (node: any) => void;
}

const ConnectionChart: React.FC<DonutChartProps> = ({
  parentid,
  data,
  linkLength = 200,
  nodeType = "",
  onNodeClick,
}) => {
  const svgRef = useRef<SVGSVGElement>(null);
  const { theme } = React.useContext(GeneralContext);
  const scale = window.innerWidth / 1920;

  useEffect(() => {
    if (!svgRef.current) return;
    d3.select(svgRef.current).selectAll("*").remove();
    d3.select("#connectionChart div").remove();

    const parentElement = document.getElementById(parentid);
    if (!parentElement) {
      // Handle the error
      console.error(`No element with ID ${parentid}`);
      return;
    }
    const rect = parentElement.getBoundingClientRect();
    const width = rect.width;
    const height = rect.height;

    // Disable double-click zoom
    d3.select(svgRef.current).on("dblclick.zoom", null);

    const tooltip = d3
      .select("#connectionChart")
      .append("div")
      .attr("id", "tooltip")
      .attr("width", 200 * scale)
      .attr("height", 200 * scale)
      .attr(
        "style",
        "position: absolute; display: none;z-index:1000; background: #fff; color: rgb(51, 51, 51); padding: 10px; font-size: 0.8rem; min-width: 100px; max-width: 200px; overflow-y: auto; pointer-events:auto",
      );

    const links = (data?.links ?? []).map((d: any) => ({ ...d }));
    let nodes = (data?.nodes ?? []).map((d: any) => ({ ...d }));

    const uniqueNodes = Array.from(new Set(nodes.map((item) => item.id))).map(
      (id) => {
        return nodes.find((item: any) => item.id === id);
      },
    );

    nodes = uniqueNodes;

    const simulation = d3
      .forceSimulation(nodes)
      .force(
        "link",
        d3
          .forceLink(links)
          .distance((_: any, i: number) => {
            return (
              linkLength * scale +
              (Math.floor(i / 30) + 1) * linkLength * 0.2 * scale
            );
          })
          .id((d: any) => d.id),
      )
      .force("charge", d3.forceManyBody().strength(-100))
      .force("center", d3.forceCenter(width / 2, height / 2))
      .on("tick", ticked);

    const svg = d3
      .select(svgRef.current)
      .attr("width", rect.width)
      .attr("height", rect.height)
      .on("dblclick", (event) => {
        event.preventDefault();
      });

    svg
      .append("rect")
      .attr("width", rect.width)
      .attr("height", rect.width)
      .attr("fill", "transparent");

    const maingroup = svg.append("g");

    const link = maingroup
      .append("g")
      .attr("stroke", theme === "dark" ? "#fff" : "#555")
      .attr("stroke-opacity", 1)
      .selectAll()
      .data(links)
      .join("line")
      .attr("stroke-width", 1)
      .attr("stroke-dasharray", (d: any) =>
        d.conntype == "prediction" ? "5,5" : "",
      )
      .attr("stroke", (d: any) => {
        if ("conntype" in d) {
          if (d.conntype == "vulnerability") return "#f00";
          if (d.conntype == "prediction") return "#ff0";
          if (d.conntype == "threat") return "#f00";
          return theme === "dark" ? "#fff" : "#555";
        }
        return theme === "dark" ? "#fff" : "#555";
      });

    const serverpic = process.env.STATIC_PATH + "/images/server.png";
    const computerpic = process.env.STATIC_PATH + "/images/computer.png";
    const internetpic = process.env.STATIC_PATH + "/images/internet.png";
    const subnetpic = process.env.STATIC_PATH + "/img/icons/subnet.png";
    const mobilepic = process.env.STATIC_PATH + "/images/mobile.png";

    const nodesize = (linkLength / 5) * scale;
    // const tooltip = svg.append('foreignobject')
    //   .attr('class', 'tooltip') // Add class for easier styling
    //   .style('visibility', 'hidden') // Initially hidden
    //   .style('position', 'absolute')
    //   .attr("width", "200")
    //   .attr("height", rect.height)
    //   .style('fill', 'var(--foreground)');
    let node: any;

    if (nodeType == "text") {
      const colors = d3
        .scaleOrdinal<string>()
        .domain(data?.nodes?.map((d) => d.id))
        .range([
          "var(--chart-1)",
          "var(--chart-2)",
          "var(--chart-3)",
          "var(--chart-4)",
          "var(--chart-5)",
          "var(--chart-6)",
          "var(--chart-7)",
          "var(--chart-8)",
          "var(--chart-9)",
          "var(--chart-10)",
          "var(--chart-11)",
          "var(--chart-12)",
        ]);

      node = maingroup
        .append("g")
        .selectAll()
        .data(nodes)
        .enter()
        .append("circle")
        .on("click", (_, d) => {
          if (d.id == "Query") return;
          onNodeClick ? onNodeClick(d.id) : "";
        })
        .attr("r", 30)
        .attr("cx", 0)
        .attr("cy", 0)
        .attr("fill", function (d) {
          return colors(d.id);
        });
    } else {
      node = maingroup
        .append("g")
        .selectAll()
        .data(nodes)
        .enter()
        .append("image")
        .on("click", (_, d) => {
          "type" in d && d.type.toLowerCase() === "subnet" && onNodeClick
            ? onNodeClick(d.id)
            : "";
        })
        .attr("xlink:href", (d) => {
          if ("type" in d) {
            if (d.type) {
              if (d.type.toLowerCase() == "server") return serverpic;
              if (d.type.toLowerCase() == "internet") return internetpic;
              if (d.type.toLowerCase() == "computer") return computerpic;
              if (d.type.toLowerCase() == "subnet") return subnetpic;
              if (d.type.toLowerCase() == "mobile") return mobilepic;
            }
            return computerpic;
          } else {
            return computerpic;
          }
        })
        // .attr("class", (d) => {
        //   return d.hasOwnProperty("type") && d.type.toLowerCase() === "subnet" ? "loadAsset" : "";
        //   // return d.hasOwnProperty("type") && d.type.toLowerCase() === "subnet" ? <ConnectionGraphDrillDown subnet={d.id} mac={} /> : "";
        // })
        .attr("width", nodesize)
        .attr("height", nodesize)
        .style("cursor", "pointer")
        .attr(
          "transform",
          "translate(-" + nodesize / 2 + ", -" + nodesize / 2 + ")",
        )
        .on("mouseover", (e, d) => {
          tooltip.transition().duration(500).style("display", "block");
          const x = e.offsetX - 30;
          const y = e.offsetY + 20;
          let hoverData = `<p>`;
          if (d.type == "subnet") {
            hoverData = `<p>
            Entity Type : ${d.type} </br>
            Subnet : ${d.id}
          </p>`;
          } else if (d.conntype == "prediction") {
            let possible_path = d.path;
            if (possible_path.length > 0) {
              possible_path = possible_path.map((obj: any) => obj["device"]);
            }
            if (possible_path.length > 1) {
              possible_path = possible_path.join(", ").slice(0, -1);
            } else {
              possible_path = possible_path[0];
            }
            let allowed_app = "";
            if (d.allowed_app.length > 1) {
              allowed_app = d.allowed_app.join(", ").slice(0, -1);
            } else {
              allowed_app = d.allowed_app[0];
            }
            hoverData += `Possible Path : ${possible_path} </br>
          Allowed Apps : ${allowed_app}`;
          } else if (d.conntype == "vulnerability") {
            hoverData += `Vulnerability: ${d.vul_id_list} </br>`;
            hoverData += `App Version: ${d.app_version} </br>`;
          } else {
            hoverData += `
            Entity Type : ${d.type} 
            ${d.hostname ? `</br>Hostname: ${d.hostname}` : ""}
            ${d.entityType != "Internet" ? `</br>IP: ${d.ip}` : ""}
            ${d.subnet ? `</br>Subnet: ${d.subnet}` : ""}
            ${d.level == 1 ? `</br>MAC Address: ${d.mac}` : ""}
            ${d.entityType == "Internet" ? `</br>Last seen IP: ${d.lastSeenIP}` : ""}
            ${d.conntype == "threat" ? `</br>Threat Score: ${d.hostThreatScore}` : ""}`;
          }
          tooltip.html(`
          ${hoverData + "</p>"}
        `);

          tooltip.style("left", `${x}px`).style("top", `${y}px`);
        })
        .on("mouseout", () => {
          tooltip.transition().duration(500).style("display", "none");
          tooltip.html(``);
        });
    }

    const zoom: any = d3
      .zoom()
      .scaleExtent([0.5, 2])
      .filter((event) => {
        // Ignore double-click events
        return !event.ctrlKey && !event.button && event.type !== "dblclick";
      })
      .on("zoom", (event) => {
        maingroup.attr("transform", event.transform);
      });

    svg.call(zoom);

    const texts = maingroup
      .append("g")
      .selectAll()
      .data(nodes)
      .enter()
      .append("text")
      .text((d: any) => {
        const count =
          d.id == "Query" || d.count == 0 || !d.count
            ? ""
            : " (" + d.count + ")";
        return d.altname ? d.altname : d.id + count;
      })
      .on("click", (_, d) => {
        if (d.id == "Query") return;
        onNodeClick ? onNodeClick(d.id) : "";
      })
      .attr("fill", theme === "dark" ? "#fff" : "#000")
      .style("cursor", "pointer")
      .style("font-size", "0.7rem")
      .style("text-anchor", "middle");
    if (nodeType != "text") {
      texts.attr("transform", `translate(0, ${30 * scale})`);
    }

    node.call(
      d3
        .drag<any, any, any>()
        .on("start", dragstarted)
        .on("drag", dragged)
        .on("end", dragended),
    );

    function ticked() {
      link
        .attr("x1", (d: any) => (d.source.x > 0 ? d.source.x : 0))
        .attr("y1", (d: any) => (d.source.y > 0 ? d.source.y : 0))
        .attr("x2", (d: any) => (d.target.x > 0 ? d.target.x : 0))
        .attr("y2", (d: any) => (d.target.y > 0 ? d.target.y : 0));

      if (nodeType == "text") {
        node
          .attr("cx", (d: any) => (d?.x > 0 ? d?.x : 0))
          .attr("cy", (d: any) => (d?.y > 0 ? d?.y : 0));
      } else {
        node
          .attr("x", (d: any) => (d.x > 0 ? d.x : 0))
          .attr("y", (d: any) => (d.y > 0 ? d.y : 0));
      }

      texts
        .attr("x", (d: any) => (d.x > 0 ? d.x : 0))
        .attr("y", (d: any) => (d.y > 0 ? d.y : 0));
    }

    function dragstarted(event: any) {
      if (!event.active) simulation.alphaTarget(0.3).restart();
      event.subject.fx = event.subject.x;
      event.subject.fy = event.subject.y;
    }

    function dragged(event: any) {
      event.subject.fx = event.x;
      event.subject.fy = event.y;
    }

    function dragended(event: any) {
      if (!event.active) simulation.alphaTarget(0);
      event.subject.fx = null;
      event.subject.fy = null;
    }
  }, [data]);

  return <svg ref={svgRef}></svg>;
};

const arePropsEqual = (
  prevProps: DonutChartProps,
  nextProps: DonutChartProps,
) => {
  // Check if non-data props are equal
  if (JSON.stringify(prevProps.data) != JSON.stringify(nextProps.data)) {
    return false;
  }

  // Deep comparison for data prop
  const prevData = prevProps.data;
  const nextData = nextProps.data;

  if (prevData === nextData) return true;
  if (!prevData || !nextData) return false;

  // Compare nodes and links arrays
  if (
    prevData.nodes?.length !== nextData.nodes?.length ||
    prevData.links?.length !== nextData.links?.length
  ) {
    return false;
  }

  // Stringify for deep comparison (can be optimized if needed)
  return (
    JSON.stringify(prevData.nodes) === JSON.stringify(nextData.nodes) &&
    JSON.stringify(prevData.links) === JSON.stringify(nextData.links)
  );
};

export default React.memo(ConnectionChart, arePropsEqual);
